import React from 'react';

/**
 * Reusable Quick Action component for approval workflows (Manual Punch, Loans, Leaves, etc.)
 * Provides single-click Quick Approve (✓) and Quick Reject (✕) actions with feedback.
 */
export default function AppQuickAction({
  status = 'Pending',
  onApprove,
  onReject,
  approveTitle = 'Quick Approve',
  rejectTitle = 'Quick Reject',
  isApproving = false,
  isRejecting = false,
  disabled = false,
  size = 'md',
  showFallback = true,
  className = '',
}) {
  const isPending = !status || status.toLowerCase() === 'pending' || status === '0';

  if (!isPending) {
    if (!showFallback) return null;
    return (
      <div className={`flex items-center justify-center select-none ${className}`}>
        {status.toLowerCase() === 'approved' || status === '1' ? (
          <span className="text-[11px] font-semibold text-emerald-600/80 inline-flex items-center gap-1 bg-emerald-50/60 px-2 py-0.5 rounded-md border border-emerald-200/40">
            <span className="material-symbols-outlined text-[13px]">done_all</span>
            Approved
          </span>
        ) : status.toLowerCase() === 'rejected' || status === '2' ? (
          <span className="text-[11px] font-semibold text-rose-500/80 inline-flex items-center gap-1 bg-rose-50/60 px-2 py-0.5 rounded-md border border-rose-200/40">
            <span className="material-symbols-outlined text-[13px]">block</span>
            Rejected
          </span>
        ) : (
          <span className="text-on-surface-variant/40">—</span>
        )}
      </div>
    );
  }

  const btnSizeClass =
    size === 'sm'
      ? 'h-6 w-6 text-[14px]'
      : 'h-7 w-7 text-[16px]';

  return (
    <div
      className={`flex items-center justify-center gap-1.5 select-none ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Quick Approve Button */}
      <button
        type="button"
        disabled={disabled || isApproving || isRejecting}
        onClick={(e) => {
          e.stopPropagation();
          onApprove?.();
        }}
        title={approveTitle}
        className={`inline-flex ${btnSizeClass} items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white transition-all shadow-2xs cursor-pointer border border-emerald-200/70 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {isApproving ? (
          <span className="material-symbols-outlined text-[14px] animate-spin">
            progress_activity
          </span>
        ) : (
          <span className="material-symbols-outlined text-[15px] font-bold">
            check
          </span>
        )}
      </button>

      {/* Quick Reject Button */}
      <button
        type="button"
        disabled={disabled || isApproving || isRejecting}
        onClick={(e) => {
          e.stopPropagation();
          onReject?.();
        }}
        title={rejectTitle}
        className={`inline-flex ${btnSizeClass} items-center justify-center rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white transition-all shadow-2xs cursor-pointer border border-rose-200/70 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {isRejecting ? (
          <span className="material-symbols-outlined text-[14px] animate-spin">
            progress_activity
          </span>
        ) : (
          <span className="material-symbols-outlined text-[15px] font-bold">
            close
          </span>
        )}
      </button>
    </div>
  );
}
